/**
 * Metal / purity coupling
 *
 * Purity (9k / 14k / 18k) only applies to the gold metals. Silver and platinum
 * carry the "none" purity value instead, and the purity picker is hidden for
 * them - see product-variant-picker.liquid, which renders that hidden state
 * server side so there is no flash on load.
 *
 * This file handles the part Liquid cannot: flipping the purity value at the
 * moment the metal changes.
 *
 *   gold -> silver / platinum   purity jumps to "none"
 *   silver / platinum -> gold   purity jumps to the default karat (9k)
 *   gold -> gold                purity is left alone, so 14k stays 14k
 *
 * The change handler runs in the capture phase, so the purity input is already
 * updated by the time <variant-selects> reads `fieldset input:checked` to build
 * its request. That keeps the whole thing to a single fetch with no
 * intermediate render.
 */
(function () {
  if (window.metalPurityCoupling) return;
  window.metalPurityCoupling = true;

  const HIDDEN_CLASS = 'product-form__input--hidden';

  /** Option values pasted into Shopify admin often carry a stray non-breaking
   *  or zero-width space, which a plain trim() leaves behind. */
  function norm(value) {
    return String(value == null ? '' : value)
      .replace(/[\u00a0\u200b-\u200d\ufeff]/g, ' ')
      .trim()
      .toLowerCase();
  }

  function matches(a, b) {
    return norm(a) === norm(b);
  }

  function config(root) {
    return {
      metalName: norm(root.dataset.metalOptionName || 'metal'),
      purityName: norm(root.dataset.purityOptionName || 'purity'),
      noPurityMetals: (root.dataset.noPurityMetals || 'silver, platinum').split(',').map(norm).filter(Boolean),
      noPurityValue: norm(root.dataset.noPurityValue || 'none'),
      defaultPurity: norm(root.dataset.defaultPurity || '9k'),
    };
  }

  /**
   * Finds an option group. Prefers the role Liquid stamped on it, and falls
   * back to reading the visible legend, so a mismatch between the configured
   * option name and the one in Shopify cannot break the feature outright.
   */
  function findGroup(root, role, name) {
    const tagged = root.querySelector('[data-option-role="' + role + '"]');
    if (tagged) return tagged;

    return (
      Array.from(root.querySelectorAll('.product-form__input')).find((group) => {
        const heading = group.querySelector('legend, .form__label');
        return heading && norm(heading.textContent).indexOf(name) !== -1;
      }) || null
    );
  }

  function purityValues(group) {
    const select = group.querySelector('select');
    return select ? Array.from(select.options) : Array.from(group.querySelectorAll('input[type="radio"]'));
  }

  function currentValue(group) {
    const select = group.querySelector('select');
    if (select) return select.value;
    const checked = group.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : '';
  }

  /** Picks `value`, falling back to the first selectable value that is not `avoid`. */
  function selectValue(group, value, avoid) {
    const options = purityValues(group);
    let target = options.find((option) => matches(option.value, value) && !option.classList.contains('disabled'));

    if (!target) {
      target = options.find(
        (option) => option.value && !matches(option.value, avoid) && !option.classList.contains('disabled')
      );
    }
    if (!target) return;

    const select = group.querySelector('select');
    if (select) {
      // <variant-selects> reads `option[selected]`, the attribute - not the property.
      Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
      target.setAttribute('selected', 'selected');
      select.value = target.value;
    } else {
      target.checked = true;
    }

    const label = group.querySelector('[data-selected-value]');
    if (label && !label.querySelector('.swatch')) label.innerHTML = target.value;
  }

  function isNoPurityMetal(settings, metalValue) {
    const metal = norm(metalValue);
    return settings.noPurityMetals.some((token) => metal.indexOf(token) !== -1);
  }

  function groupsFor(root) {
    const settings = config(root);
    const metal = findGroup(root, 'metal', settings.metalName);
    const purity = findGroup(root, 'purity', settings.purityName);
    if (!metal || !purity || metal === purity) return null;
    return { settings: settings, metal: metal, purity: purity };
  }

  /** Brings the purity picker in line with whichever metal is currently selected. */
  function sync(root) {
    const found = groupsFor(root);
    if (!found) return;
    found.purity.classList.toggle(HIDDEN_CLASS, isNoPurityMetal(found.settings, currentValue(found.metal)));
  }

  function syncAll() {
    document.querySelectorAll('variant-selects').forEach(sync);
  }

  document.addEventListener(
    'change',
    function (event) {
      const input = event.target;
      if (!input || !input.closest) return;

      const root = input.closest('variant-selects');
      if (!root) return;

      const found = groupsFor(root);
      if (!found || !found.metal.contains(input)) return;

      const needsNone = isNoPurityMetal(found.settings, input.value);

      if (needsNone) {
        selectValue(found.purity, found.settings.noPurityValue);
      } else if (matches(currentValue(found.purity), found.settings.noPurityValue)) {
        selectValue(found.purity, found.settings.defaultPurity, found.settings.noPurityValue);
      }

      // Toggle straight away so the picker does not linger until the fetch
      // returns. The re-rendered markup carries the same state.
      found.purity.classList.toggle(HIDDEN_CLASS, needsNone);
    },
    true
  );

  // <variant-selects> is replaced wholesale after every variant change, so
  // re-apply once the new markup has landed.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, syncAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }
})();
